'use client'

import { useEffect, useState } from 'react'
import { useAuth as useClerkAuth, useClerk, useUser as useClerkUser } from '@clerk/nextjs'
import { useRouter } from 'next/navigation'
import { getDbUserProfile, type DbUserProfile } from '@/lib/actions/profile-actions'

/**
 * Client-side hook for auth state with database user ID.
 *
 * Uses Clerk for authentication and looks up the database user ID
 * by clerk_id via server action for compatibility with existing queries.
 *
 * @returns User object with database UUID, loading state, and signOut function
 */
export function useAuth() {
  const { isLoaded, isSignedIn, userId: clerkId } = useClerkAuth()
  const { user: clerkUser } = useClerkUser()
  const { signOut: clerkSignOut } = useClerk()
  const router = useRouter()
  const [user, setUser] = useState<DbUserProfile | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadUser() {
      if (!isLoaded) return

      if (!isSignedIn || !clerkId) {
        setUser(null)
        setLoading(false)
        return
      }

      try {
        // Look up database user for the current session via server action
        // (server derives clerkId from the session itself, not this param)
        const profile = await getDbUserProfile()

        if (!profile) {
          console.error('[useAuth] Failed to load profile')
          setUser(null)
        } else {
          // Override email with Clerk's email if available (more current)
          const email = clerkUser?.emailAddresses?.[0]?.emailAddress ?? profile.email
          setUser({ ...profile, email })
        }
      } catch (err) {
        console.error('[useAuth] Unexpected error:', err)
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    loadUser()
  }, [isLoaded, isSignedIn, clerkId, clerkUser])

  const signOut = async () => {
    await clerkSignOut()
    router.push('/login')
  }

  return {
    user,
    loading: !isLoaded || loading,
    signOut,
    isSignedIn: isSignedIn ?? false,
  }
}
